"use client";

import React, { useRef, useEffect } from "react";
import { isValid } from "@/lib/utils";
import { useToast } from "@/components/ui/use-toast";
import captchImg from "@/assets/images/static/captch.webp";
import CustomInput from "@/components/ui/customInput";
import { Textarea } from "@/components/ui/textarea";
import DynamicImage from "@/components/DynamicImages/DynamicImage";
import { Controller, useForm } from "react-hook-form";
import { allBrandList } from "@/components/BrandDetail/arraybrandlist";
import { useContactUsMutation } from "@/redux/apiSlices/formApi";
import { Button } from "../ui/button";

const ContactSpecialist = ({ brandName }) => {
  const { toast } = useToast();
  const formRef = useRef(null); // ✅ Create a ref for the form
  const DataResponse = allBrandList.find((item) => item[brandName])?.[brandName];

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm();

  const [contactUs, { data, isSuccess, isLoading: isUserLoading, isError, error: errMsg }] = useContactUsMutation();
  useEffect(() => {
    if (isSuccess) {
      if (data?.success) {
        toast({ title: "The email has been sent successfully." })
      }
    }
    if (isError) {
      toast({
        title: errMsg?.data?.msg,
      });
    }
  }, [isSuccess, isError]);

  const onSubmit = async (data) => {
    if (!formRef.current) return;

    try {
      const subject = `Online Brand Inquiry - ${brandName || "Unknown Company Name"}`;
      const formType=`Brand Inquiry Form`
      const description  = `You have received a new message from ${data.first_name} ${data.last_name} via our online ${brandName} brand page. Please reach out to the customer for further assistance.`
      const formData = { ...data, subject, description, formType };
    
       await contactUs(formData);
      reset(); // ✅ Reset form fields after successful submission
    } catch (error) {
      toast({
        title: error,
      });
    }
  };

  const brandNameMapping = new Map([
    ["U_Line", "U-Line"],
    ["Lg", "LG"],
    ["Lg_Studio", "LG Studio"],
    ["Kitchenaid", "KitchenAid"],
    ["Ge_Profile", "GE Profile"],
    ["Ge_Appliances", "GE"],
    ["Frigidaire_Pro", "Frigidaire Professional"],
  ]);

  const formattedBrandName =
    brandNameMapping.get(brandName) ||
    brandName.replace(/Appliances/g, "").replace(/_/g, " ").trim();

  return (
    <>
      <div className="max-w-[932px] mx-auto my-0 pb-[40px] pt-[30px] px-4">
        <h3 className="text-[30px] smd:text-[24px] mb-10 leading-[43px] font-bold text-center text-[#374151]">
          Contact a {formattedBrandName} Specialist
        </h3>

        {/* ✅ Attach ref to form */}
        <form ref={formRef} onSubmit={handleSubmit(onSubmit)}>
          <div className="mb-5">
            <div className="grid grid-cols-2 gap-x-5 sm:grid-cols-1">
              <input type="hidden" name="subject" value={`Online Brand Inquiry - ${brandName || "Unknown Company Name"}`} />
              <Controller
                name="first_name"
                control={control}
                rules={{ required: "Please enter first name" }}
                render={({ field }) => (
                  <CustomInput
                    name="first_name" // ✅ Ensure correct name for EmailJS
                    labelClassName=" bg-white"
                    label="First Name*"
                    maxLength={25}
                    isInvalid={isValid("first_name", errors)}
                    errorMessage={errors.first_name?.message}
                    {...field}
                  />
                )}
              />
              <Controller
                name="last_name"
                control={control}
                rules={{ required: "Please enter last name" }}
                render={({ field }) => (
                  <CustomInput
                    name="last_name" // ✅ Ensure correct name for EmailJS
                    labelClassName=" bg-white"
                    label="Last Name*"
                    maxLength={25}
                    isInvalid={isValid("last_name", errors)}
                    errorMessage={errors.last_name?.message}
                    {...field}
                  />
                )}
              />
              <Controller
                name="email"
                control={control}
                rules={{
                  required: "Please enter an email",
                  pattern: {
                    value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/,
                    message: "Please enter a valid email",
                  },
                }}
                render={({ field }) => (
                  <CustomInput
                    name="email" // ✅ Ensure correct name for EmailJS
                    labelClassName="bg-white"
                    label="Email*"
                    maxLength={1000}
                    isInvalid={isValid("email", errors)}
                    errorMessage={errors.email?.message}
                    {...field}
                  />
                )}
              />
              <Controller
                name="mobile"
                control={control}
                rules={{
                  required: "Phone number is required",
                  validate: (value) => value.trim() !== "" || "Only spaces are not allowed",
                  pattern: {
                    value: /^[0-9]+$/, // Only numbers
                    message: "Phone number must contain only digits",
                  },
                  minLength: {
                    value: 10, // Adjust based on your needs
                    message: "Please enter a valid 10-digit",
                  },
                  maxLength: {
                    value: 10, // Adjust based on your needs
                    message: "Please enter a valid 10-digit",
                  },
                }}
                render={({ field }) => (
                  <CustomInput
                    labelClassName="bg-white"
                    label="Phone Number*"
                    isInvalid={isValid("mobile", errors)}
                    errorMessage={errors.mobile?.message}
                    {...field}
                  />
                )}
              />
             
            </div>

            <div className="grid grid-cols-1 gap-x-5 sm:grid-cols-1">
              <Controller
                name="message"
                control={control}
                rules={{ 
                  required: "Please enter your message", 
                  maxLength: { value: 1000, message: "Message cannot exceed 1000 characters" }
                }}
                render={({ field }) => (
                  <Textarea
                    name="message" // ✅ Ensure correct name for EmailJS
                    placeholder="Your Message"
                    maxLength={1000}
                    isInvalid={isValid("message", errors)}
                    errorMessage={errors.question?.message}
                    {...field}
                  />
                )}
              />
            </div>
          </div>

          <div className="mt-8 w-full flex justify-between gap-[43px] mob:flex-col mob:gap-0">
            <span className="inline-block float-left w-[267px] h-[60px] md:block md:float-none md:mb-8 md:m-auto">
              <DynamicImage
                src={captchImg}
                className="w-full h-full object-cover md:object-top"
                alt="Captcha"
              />
            </span>
            <Button
              type="submit"
              className="flex-1 md:mb-[32px] bg-[#0b223c] text-white px-6 py-3 rounded-[8px] min-h-[50px]"
            >
              Submit Now
            </Button>
          </div>
        </form>
      </div>

      {/* {DataResponse?.contentList.length !==0 && <div className="border-t-2">
        <div className="max-w-[1440px] px-5 mx-auto my-0 pt-[50px]">
          {DataResponse?.contentList.map((item, index) => (
            <div key={index}>
              <h1 className="font-semibold text-[22px] mb-4 mt-8">
                {item?.mainHeading}
              </h1>
              <div className="mb-5">
                <h2 className="font-semibold text-[17px] mb-4">{item?.title}</h2>
                <p>{item?.descriptionTxt}</p>
              </div>
            </div>
          ))}
        </div>
      </div>} */}
    </>
  );
};

export default ContactSpecialist;